/**
 * _exemplar.js — the reference task every other prompt task follows.
 * (Also exported from packaging.js as the first Packaging task.)
 *
 * TASK SCHEMA
 * -----------
 * id              kebab-case, globally unique. Route: /ai-lab/prompt-builder/:taskId
 * category        one of the CATEGORIES ids in categories.js
 * title           verb-first, e.g. "Create Packaging Concept"
 * description     one sentence of what the user gets
 * outputType      "text" | "image" | "video" | "audio"  → drives the "Open in…" buttons
 * recommendedTools array of tool ids from aiTools.js, best first
 * estimatedTime   human string, e.g. "2 min to fill · 1 min in ChatGPT"
 * fields[]        see FIELD SCHEMA
 * template        string using the renderTemplate.js mini-language
 * tips[]          (optional) 1–3 short tips shown under the output
 *
 * FIELD SCHEMA
 * ------------
 * id              camelCase, unique within the task; referenced as {{id}} in template
 * label           short label
 * type            "text" | "textarea" | "tags" | "select" | "number" | "url"
 *                 ("tags" = comma-separated list; use {{id|list}} / {{id|join}} in template)
 * placeholder     example text shown in the input
 * help            (optional) one-line helper under the input
 * required        (optional) boolean
 * brandKey        (optional) key from brandProfileSchema.js that auto-fills this field
 * options         (select only) array of strings
 * default         (optional) default value
 * span            (optional) "full" to make the input span both columns on desktop
 */
export const PACKAGING_CONCEPT_TASK = {
  id: "packaging-concept",
  category: "packaging",
  title: "Create Packaging Concept",
  description: "Three distinct, commercially viable packaging directions — each with logo, type, color, hierarchy, flavor system and a ready-to-use image-generation prompt.",
  outputType: "text",
  recommendedTools: ["chatgpt", "claude", "gemini"],
  estimatedTime: "2 min to fill · 1 min in ChatGPT",
  fields: [
    { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
    { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
    { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
    { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
    { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
    { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
    { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal, Bold", brandKey: "visualStyle" },
    { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; excessive neon colors", brandKey: "avoid" },
    { id: "format", label: "Package format", type: "select", options: ["12 oz sleek can", "16 oz sleek can", "12 oz standard can", "Glass bottle", "PET bottle", "Stand-up pouch", "Flow-wrap bar", "Folding carton", "Jar", "Tub", "Other (describe in positioning)"], default: "16 oz sleek can" },
    { id: "conceptCount", label: "Number of concepts", type: "select", options: ["1", "2", "3", "5"], default: "3" },
    { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "Leave blank to let AI propose", brandKey: "brandColors" },
    { id: "market", label: "Primary market", type: "select", options: ["U.S. national retail", "U.S. regional / natural grocery", "U.S. convenience", "Canada", "UK / Europe", "DTC / e-commerce only"], default: "U.S. national retail" },
  ],
  template: `Act as a senior CPG packaging creative director specializing in {{category|lower}} brands.

Create {{conceptCount}} distinct packaging design concept{{#if conceptCount!=1}}s{{/if}} for {{brandName}}, {{category|article}} targeting {{targetConsumer}}.

BRAND POSITIONING
{{positioning}}

{{#if claims}}
KEY PRODUCT ATTRIBUTES
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}.
The design should feel clearly differentiated from these competitors while remaining appropriate for the modern {{category|lower}} category.
{{/if}}

VISUAL DIRECTION
Create a visual system that feels:
{{style|list}}
{{#unless style}}
- Premium
- Modern
- Distinctive
{{/unless}}
{{#if brandColors}}
Work with the existing brand colors where possible: {{brandColors|join}}.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Cluttered packaging
- Generic category clichés
{{/if}}
{{#unless avoid}}
AVOID
- Cluttered packaging
- Generic category clichés
{{/unless}}

PACKAGE FORMAT
{{format}}

FOR EACH CONCEPT PROVIDE
1. Creative concept name
2. Design rationale (why it wins on shelf against the competitive set)
3. Logo treatment
4. Typography direction (2 typefaces max, with hierarchy)
5. Color system (primary, secondary, accent — with hex codes)
6. Graphic elements and illustration / photography style
7. Front-of-pack hierarchy (what the eye reads 1st, 2nd, 3rd)
8. Flavor / variant differentiation system
9. Recommended material and finish (e.g. matte, soft-touch, spot gloss, metallic)
10. A detailed image-generation prompt (for ChatGPT image generation, Midjourney or Firefly) that creates a realistic {{format}} product mockup on a clean studio background

Finish with a one-paragraph recommendation on which concept to develop first and why.

The packaging must appear commercially viable and capable of competing in {{market}}.`,
  tips: [
    "Paste the result into ChatGPT or Claude first. Then copy concept #10 (the image prompt) into an image tool to see it.",
    "Ask a follow-up: “Push concept 2 further — make it 30% more premium and show me 3 flavor variants.”",
  ],
};
